// useAuth.ts
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import {
  signIn,
  confirmSignIn,
  signUp,
  confirmSignUp,
  resendSignUpCode,
  signOut,
  updatePassword,
  resetPassword,
  confirmResetPassword,
  getCurrentUser,
} from "aws-amplify/auth";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

export const UserTypes = {
  neweyes: 1,
  agency: 2,
  caseworker: 3,
  registered: 5,
};

interface AuthHook {
  user: any;
  authError: string | null;
  loading: boolean;
  recaptchaSignIn: (username: string, password: string) => Promise<any>;
  signIn: (username: string, password: string) => Promise<any>;
  confirmSignIn: (challengeResponse?: string) => Promise<any>;
  signUp: (
    username: string,
    password: string,
    userAttributes: { [key: string]: string }
  ) => Promise<any>;
  confirmSignUp: (username: string, confirmationCode: string) => Promise<any>;
  resendSignUpCode: (username: string) => Promise<any>;

  signOut: () => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<any>;
  forgotPassword: (username: string) => Promise<any>;
  forgotPasswordConfirm: (
    username: string,
    confirmationCode: string,
    newPassword: string
  ) => Promise<boolean>;
  getCaptchaToken: (action: string) => Promise<string | null>
}

const AuthContext = createContext<AuthHook>({
  user: null,
  authError: null,
  loading: false,
  recaptchaSignIn: async () => null,
  signIn: async () => null,
  confirmSignIn: async () => null,
  signUp: async () => null,
  confirmSignUp: async () => null,
  resendSignUpCode: async () => null,
  signOut: async () => { },
  changePassword: async () => null,
  forgotPassword: async () => null,
  forgotPasswordConfirm: async () => false,
  getCaptchaToken: async () => null,
});

interface AuthProviderProps {
  // App might have its own appUser with user profile and preferences
  loadUserAppProfile?: (cognitoUser: any) => Promise<any>;
  children: ReactNode;
}

export const AuthProvider = ({ loadUserAppProfile, children }: AuthProviderProps) => {
  const auth = useAuthProvider(loadUserAppProfile ?? (async (cognitoUser) => Promise.resolve(cognitoUser)));
  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);

const useAuthProvider = (loadUserAppProfile: (cognitoUser: any) => Promise<any> | undefined): AuthHook => {
  const [userAppProfile, setUserAppProfile] = useState<any>(null);
  const [authError, setAuthError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const reloadUserAppProfile = async (pageLoading: boolean = false) => {
    try {
      const cognitoUser = await getCurrentUser();
      const profile = await loadUserAppProfile(cognitoUser);
      setUserAppProfile(profile);
      console.log(profile);
    } catch (error: any) {
      if (!pageLoading) {
        setAuthError(error.code && error.code === "ERR_NETWORK"
          ? "Unable to connect to server. Please check your internet connection."
          : error.message);
      }
    }
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      await reloadUserAppProfile(true);
    };

    fetchUserProfile();
  }, [])

  // Helper to execute recaptcha for a given action
  const getCaptchaToken = async (action: string): Promise<string | null> => {
    if (!executeRecaptcha) {
      console.warn("Recaptcha not available yet");
      return null;
    }
    try {
      const token = await executeRecaptcha(action);
      return token;
    } catch (error: any) {
      console.error("Error executing recaptcha", error.message);
      return null;
    }
  };

  const handleRecaptchaSignIn = async (username: string, password: string) => {
    setLoading(true);
    setAuthError(null);
    try {
      let result = await signIn({
        username,
        password,
        options: { authFlowType: "CUSTOM_WITH_SRP" },
      });

      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED") {
        // You need to prompt the user for a new password, then call:
        // await confirmSignIn({ challengeResponse: newPassword });
        // Optionally with userAttributes in options if you need to set them.
        // After that confirmSignIn will either sign them in or return another step.
        // await confirmSignIn({
        //   challengeResponse: newPassword,
        //   options: {
        //     // optionally pass userAttributes if needed
        //   },
        // });
      }

      if (result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
        let recaptchaToken = (await getCaptchaToken("customSignIn")) || "";
        result = await confirmSignIn({
          challengeResponse: recaptchaToken,
        });
        if (result.isSignedIn) {
          reloadUserAppProfile();
        }
      }
      return result;
    } catch (error: any) {
      // Another user might have been logged in
      if (error.name === "UserAlreadyAuthenticatedException") {
        await handleSignOut();
        setAuthError("Error signing in. Try again.");
      } else {
        setAuthError(error.message || "Error signing in");
      }

      return null;
    } finally {
      setLoading(false);
    }
  }

  // Sign in with custom auth flow.
  const handleSignIn = async (username: string, password: string) => {
    setLoading(true);
    setAuthError(null);
    try {
      // Use a custom auth flow (ensure your app client is configured accordingly)
      const result = await signIn({
        username,
        password,
        options: { authFlowType: "CUSTOM_WITH_SRP" },
      });
      if (result.isSignedIn) {
        reloadUserAppProfile();
      }
      return result;
    } catch (error: any) {
      setAuthError(error.message || "Error signing in");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Confirm sign in: if no challenge response is provided, fetch a captcha token.
  const handleConfirmSignIn = async (challengeResponse?: string) => {
    setLoading(true);
    setAuthError(null);
    try {
      let response = challengeResponse;
      if (!response) {
        response = (await getCaptchaToken("customSignIn")) || "";
      }
      const result = await confirmSignIn({ challengeResponse: response });
      if (result.isSignedIn) {
        reloadUserAppProfile();
      }
      return result;
    } catch (error: any) {
      setAuthError(error.message || "Error confirming sign in");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Sign up: automatically attach a recaptcha token via clientMetadata.
  const handleSignUp = async (
    username: string,
    password: string,
    userAttributes: { [key: string]: string }
  ) => {
    setLoading(true);
    setAuthError(null);
    try {
      const captchaToken = await getCaptchaToken("signUp");
      const result = await signUp({
        username,
        password,
        options: {
          userAttributes,
          clientMetadata: { captchaToken: captchaToken || "" },
        },
      });
      return result;
    } catch (error: any) {
      setAuthError(error.message || "Error signing up");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSignUp = async (
    username: string,
    confirmationCode: string
  ) => {
    setLoading(true);
    setAuthError(null);
    try {
      const result = await confirmSignUp({ username, confirmationCode });
      return result;
    } catch (error: any) {
      setAuthError(error.message || "Error confirming sign up");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleResendSignUpCode = async (username: string) => {
    setLoading(true);
    setAuthError(null);
    try {
      const result = await resendSignUpCode({ username });
      return result;
    } catch (error: any) {
      setAuthError(error.message || "Error resending confirmation code");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    setLoading(true);
    setAuthError(null);
    try {
      await signOut();
      setUserAppProfile(null);
    } catch (error: any) {
      setAuthError(error.message || "Error signing out");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (
    oldPassword: string,
    newPassword: string
  ) => {
    setLoading(true);
    setAuthError(null);
    try {
      if (!userAppProfile) throw new Error("User is not signed in");
      const result = await updatePassword({ oldPassword, newPassword });
      return result;
    } catch (error: any) {
      setAuthError(error.message || "Error changing password");
      return null;
    } finally {
      setLoading(false);
    }
  };

  // Forgot password: automatically attach a recaptcha token.
  const handleForgotPassword = async (username: string) => {
    setLoading(true);
    setAuthError(null);
    try {
      const captchaToken = await getCaptchaToken("forgotPassword");
      const result = await resetPassword({
        username,
        options: { clientMetadata: { captchaToken: captchaToken || "" } },
      });
      return result;
    } catch (error: any) {
      setAuthError(error.message || "Error initiating forgot password");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordConfirm = async (
    username: string,
    confirmationCode: string,
    newPassword: string
  ) => {
    setLoading(true);
    setAuthError(null);
    try {
      await confirmResetPassword({
        username,
        confirmationCode,
        newPassword,
      });
      return true
    } catch (error: any) {
      console.log(error)
      if (error.name === "InvalidPasswordException") {
        setAuthError(`Password does not conform to policy:
          - Contains at least 8 character(s)
          - Contains at least 1 number
          - Contains at least 1 special character
          - Contains at least 1 uppercase letter
          - Contains at least 1 lowercase letter`);
      } else {
        setAuthError(error.message || "Error resetting password");
      }
      return false;
    } finally {
      setLoading(false);
    }
  };

  return {
    user: userAppProfile,
    authError,
    loading,
    recaptchaSignIn: handleRecaptchaSignIn,
    signIn: handleSignIn,
    confirmSignIn: handleConfirmSignIn,
    signUp: handleSignUp,
    confirmSignUp: handleConfirmSignUp,
    resendSignUpCode: handleResendSignUpCode,
    signOut: handleSignOut,
    changePassword: handleChangePassword,
    forgotPassword: handleForgotPassword,
    forgotPasswordConfirm: handleForgotPasswordConfirm,
    getCaptchaToken,
  };
};

export default useAuth;

